import React, { useState } from 'react';
import { Box, Container, Typography, Stack, Button, Divider } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CookieIcon from '@mui/icons-material/Cookie';
import BarChartIcon from '@mui/icons-material/BarChart';
import SecurityIcon from '@mui/icons-material/Security';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { useNavigate } from 'react-router-dom';

const CONSENT_KEY = 'smcla_analytics_consent';

const LAST_UPDATED = 'May 31, 2025';

const sectionStyle = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 15,
  color: '#9499b0',
  lineHeight: 1.8,
};

const h2Style = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: 700,
  fontSize: { xs: '1.2rem', md: '1.4rem' },
  color: '#f0f0f8',
  letterSpacing: '-0.01em',
  mb: 2,
  mt: 0,
};

const tableHeaderCell = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: 700,
  fontSize: 12,
  color: '#5ce1e6',
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  pb: 1.5,
};

const tableCell = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 13,
  color: '#9499b0',
  lineHeight: 1.6,
};

function Section({ id, icon, title, children }: { id: string; icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <Box id={id} sx={{ py: 5, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ color: '#5ce1e6', display: 'flex' }}>{icon}</Box>
        <Typography sx={h2Style}>{title}</Typography>
      </Box>
      {children}
    </Box>
  );
}

function TableRow({ cols, header }: { cols: string[]; header?: boolean }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 2fr 1fr', gap: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      {cols.map((c, i) => (
        <Typography key={i} sx={header ? tableHeaderCell : tableCell}>{c}</Typography>
      ))}
    </Box>
  );
}

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const [consentReset, setConsentReset] = useState(false);

  const handleResetConsent = () => {
    localStorage.removeItem(CONSENT_KEY);
    setConsentReset(true);
    setTimeout(() => window.location.reload(), 800);
  };

  const currentConsent = localStorage.getItem(CONSENT_KEY);

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Privacy & Cookie Policy" description="Smart Media Clarity's full privacy and cookie policy — what data we collect on this website, how analytics work, and how to manage your preferences." path="/privacy-policy" />
      <Header onBookDemo={() => navigate('/contact')} />

      {/* Hero */}
      <Box sx={{ pt: { xs: 10, md: 14 }, pb: 6, px: 3 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <LockIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Legal
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2, lineHeight: 1.1 }}>
            Privacy &amp; Cookie Policy
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, maxWidth: 600, mb: 2 }}>
            This policy explains what information Smart Media Clarity LA, LLC ("SMCLA", "we", "us") collects when you visit this website, how we use it, and the choices you have — including the right to opt out of analytics at any time.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.5)' }}>
            Last updated: {LAST_UPDATED}
          </Typography>
        </Container>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      <Box sx={{ py: { xs: 2, md: 4 }, px: 3 }}>
        <Container maxWidth="md">

          {/* 1. Information We Collect */}
          <Section id="information" icon={<SecurityIcon sx={{ fontSize: 20 }} />} title="1. Information We Collect">
            <Typography sx={sectionStyle} mb={2}>
              When you visit this website we may collect the following categories of information:
            </Typography>
            <Stack spacing={1.5}>
              {[
                { label: 'Usage data', detail: 'Pages visited, time on page, referring URL, browser type, operating system, screen resolution, and general geographic region (country/city level). Collected automatically if you accept analytics.' },
                { label: 'Form submissions', detail: 'If you submit the "Book a Demo" form, we collect your name, organization, work email, and optional phone number. This data is used only to respond to your inquiry.' },
                { label: 'Cookies & local storage', detail: 'We use a single analytics tool (Google Analytics 4) and store your cookie preference in your browser\'s localStorage. See the Cookie Details section below.' },
              ].map(({ label, detail }) => (
                <Box key={label} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#f0f0f8', mb: 0.75 }}>{label}</Typography>
                  <Typography sx={{ ...sectionStyle, fontSize: 13 }}>{detail}</Typography>
                </Box>
              ))}
            </Stack>
            <Typography sx={{ ...sectionStyle, fontSize: 13, mt: 2.5, color: 'rgba(148,153,176,0.6)' }}>
              We do not collect sensitive personal data, payment information, or any data relating to children's identities through this website.
            </Typography>
          </Section>

          {/* 2. Cookies */}
          <Section id="cookies" icon={<CookieIcon sx={{ fontSize: 20 }} />} title="2. Cookies &amp; Similar Technologies">
            <Typography sx={sectionStyle} mb={3}>
              Cookies are small files placed on your device. We use two types:
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#ff9f43', mb: 2, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Essential (always on)
              </Typography>
              <Box sx={{ bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, pt: 2, display: { xs: 'none', md: 'block' } }}>
                  <TableRow header cols={['Name', 'Type', 'Purpose', 'Duration']} />
                </Box>
                <Box sx={{ px: 2.5, pb: 2 }}>
                  <TableRow cols={['smcla_analytics_consent', 'localStorage', 'Stores your accept/deny choice for analytics cookies so you are not asked again.', 'Persistent']} />
                </Box>
              </Box>
            </Box>

            <Box>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#5ce1e6', mb: 2, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Analytics (only with your consent)
              </Typography>
              <Box sx={{ bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, pt: 2, display: { xs: 'none', md: 'block' } }}>
                  <TableRow header cols={['Name', 'Type', 'Purpose', 'Duration']} />
                </Box>
                <Box sx={{ px: 2.5, pb: 2 }}>
                  <TableRow cols={['_ga', 'Cookie', 'Google Analytics — distinguishes unique visitors.', '2 years']} />
                  <TableRow cols={['_ga_*', 'Cookie', 'Google Analytics — maintains session state.', '2 years']} />
                  <TableRow cols={['_gid', 'Cookie', 'Google Analytics — distinguishes users, expires quickly.', '24 hours']} />
                </Box>
              </Box>
              <Typography sx={{ ...sectionStyle, fontSize: 12, mt: 1.5, color: 'rgba(148,153,176,0.5)' }}>
                Google Analytics is operated by Google LLC. Their privacy policy is available at policies.google.com/privacy.
              </Typography>
            </Box>
          </Section>

          {/* 3. How We Use Information */}
          <Section id="use" icon={<BarChartIcon sx={{ fontSize: 20 }} />} title="3. How We Use Your Information">
            <Stack spacing={1.5}>
              {[
                { purpose: 'Improve this website', detail: 'Analytics data (pages visited, time on page, button clicks) helps us understand what content is useful and which parts of the product resonate with visitors.' },
                { purpose: 'Respond to demo requests', detail: 'Form submission data (name, email, organization) is used solely to follow up on your inquiry. We do not sell, rent, or share it with third parties for marketing purposes.' },
                { purpose: 'Understand conversion', detail: 'We track which pages and buttons lead to demo requests. This data is aggregated — we are not building individual profiles.' },
                { purpose: 'Legal compliance', detail: 'We may retain and disclose information if required by law, court order, or to protect the rights and safety of SMCLA and its users.' },
              ].map(({ purpose, detail }) => (
                <Box key={purpose} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#5ce1e6', mt: 1, flexShrink: 0 }} />
                  <Typography sx={sectionStyle}>
                    <Box component="span" sx={{ fontWeight: 600, color: '#d0d0e0' }}>{purpose} — </Box>
                    {detail}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Section>

          {/* 4. Your Rights */}
          <Section id="rights" icon={<SecurityIcon sx={{ fontSize: 20 }} />} title="4. Your Rights &amp; Choices">
            <Typography sx={sectionStyle} mb={3}>
              Depending on where you are located, you may have the following rights regarding your personal data:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
              {[
                { right: 'Access', detail: 'Request a copy of the personal data we hold about you.' },
                { right: 'Deletion', detail: 'Request that we delete your personal data. For analytics data, opt out using the preference panel below.' },
                { right: 'Correction', detail: 'Request that we correct inaccurate personal data.' },
                { right: 'Portability', detail: 'Request your data in a machine-readable format.' },
                { right: 'Opt-out of sale', detail: 'We do not sell personal data. This right is noted for CCPA compliance — there is nothing to opt out of.' },
                { right: 'Withdraw consent', detail: 'You can withdraw analytics consent at any time using the panel below. It takes effect immediately.' },
              ].map(({ right, detail }) => (
                <Box key={right} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#f0f0f8', mb: 0.5 }}>{right}</Typography>
                  <Typography sx={{ ...sectionStyle, fontSize: 13 }}>{detail}</Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ ...sectionStyle, fontSize: 13 }}>
              To exercise any of these rights, email us at{' '}
              <Box component="a" href="mailto:privacy@smartmediaclarity.com" sx={{ color: '#5ce1e6', textDecoration: 'none' }}>
                privacy@smartmediaclarity.com
              </Box>. We will respond within 30 days.
            </Typography>
          </Section>

          {/* 5. Manage Preferences */}
          <Section id="preferences" icon={<CookieIcon sx={{ fontSize: 20 }} />} title="5. Manage Your Cookie Preferences">
            <Typography sx={sectionStyle} mb={3}>
              Your current analytics preference:{' '}
              <Box component="span" sx={{ fontWeight: 700, color: currentConsent === 'true' ? '#5ce1e6' : currentConsent === 'false' ? '#ff6b6b' : '#9499b0' }}>
                {currentConsent === 'true' ? 'Accepted' : currentConsent === 'false' ? 'Denied' : 'Not yet chosen'}
              </Box>
            </Typography>

            <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8', mb: 1 }}>
                Reset cookie preferences
              </Typography>
              <Typography sx={{ ...sectionStyle, fontSize: 13, mb: 3 }}>
                Clicking the button below clears your stored preference and reloads the page. The consent banner will appear again so you can make a new choice.
              </Typography>
              {consentReset ? (
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#5ce1e6' }}>
                  Preferences cleared — reloading…
                </Typography>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleResetConsent}
                  sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#9499b0', borderColor: 'rgba(255,255,255,0.15)', borderRadius: 1.5, px: 2.5, py: 1, '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)' } }}
                >
                  Reset Cookie Preferences
                </Button>
              )}
            </Box>

            <Typography sx={{ ...sectionStyle, fontSize: 13, mt: 2.5 }}>
              You can also disable cookies entirely in your browser settings. See your browser's help documentation for instructions. Note that disabling cookies may affect the functionality of some websites.
            </Typography>
          </Section>

          {/* 6. Data Retention */}
          <Section id="retention" icon={<SecurityIcon sx={{ fontSize: 20 }} />} title="6. Data Retention">
            <Stack spacing={1.5}>
              {[
                { type: 'Analytics data', period: 'Up to 26 months in Google Analytics, then automatically deleted. You can opt out at any time using the preference panel above.' },
                { type: 'Demo request form submissions', period: 'Retained for up to 2 years or until you request deletion. Used solely to follow up on your inquiry.' },
                { type: 'Cookie preference (localStorage)', period: 'Stored in your browser until you clear it manually or use the Reset button above. Never transmitted to our servers.' },
              ].map(({ type, period }) => (
                <Box key={type} sx={{ display: 'flex', gap: 2, p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2 }}>
                  <Box sx={{ flexShrink: 0, pt: 0.3 }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#5ce1e6' }} />
                  </Box>
                  <Typography sx={sectionStyle}>
                    <Box component="span" sx={{ fontWeight: 600, color: '#d0d0e0' }}>{type}: </Box>
                    {period}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Section>

          {/* 7. Third Parties */}
          <Section id="third-parties" icon={<SecurityIcon sx={{ fontSize: 20 }} />} title="7. Third-Party Services">
            <Typography sx={sectionStyle} mb={3}>
              This website uses one third-party analytics service, activated only with your consent:
            </Typography>
            <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8', mb: 1 }}>
                Google Analytics 4
              </Typography>
              <Typography sx={{ ...sectionStyle, fontSize: 13, mb: 1.5 }}>
                Provided by Google LLC (1600 Amphitheatre Parkway, Mountain View, CA 94043, USA). We use GA4 to understand aggregate website usage — pages visited, buttons clicked, and conversions. IP addresses are anonymized. Data is processed in the US under Google's standard contractual clauses.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.5)' }}>
                Google Privacy Policy: policies.google.com/privacy · Google Analytics opt-out browser add-on: tools.google.com/dlpage/gaoptout
              </Typography>
            </Box>
            <Typography sx={{ ...sectionStyle, fontSize: 13, mt: 2.5 }}>
              We do not use advertising networks, social media pixels, retargeting tools, or any other third-party tracking on this website.
            </Typography>
          </Section>

          {/* 8. Changes */}
          <Section id="changes" icon={<SecurityIcon sx={{ fontSize: 20 }} />} title="8. Changes to This Policy">
            <Typography sx={sectionStyle}>
              We may update this policy from time to time. When we do, we will update the "Last updated" date at the top of this page. Material changes will be noted more prominently. Continued use of this website after a policy update constitutes your acceptance of the revised policy.
            </Typography>
          </Section>

          {/* 9. Contact */}
          <Section id="contact" icon={<MailOutlineIcon sx={{ fontSize: 20 }} />} title="9. Contact">
            <Typography sx={sectionStyle} mb={3}>
              For any questions about this policy or to exercise your data rights:
            </Typography>
            <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3 }}>
              <Stack spacing={1.5}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8' }}>
                  Smart Media Clarity LA, LLC
                </Typography>
                <Typography sx={{ ...sectionStyle, fontSize: 13 }}>
                  Privacy inquiries:{' '}
                  <Box component="a" href="mailto:privacy@smartmediaclarity.com" sx={{ color: '#5ce1e6', textDecoration: 'none' }}>
                    privacy@smartmediaclarity.com
                  </Box>
                </Typography>
                <Typography sx={{ ...sectionStyle, fontSize: 13 }}>
                  General contact:{' '}
                  <Box component="a" href="mailto:admin@smartmediaclarity.com" sx={{ color: '#5ce1e6', textDecoration: 'none' }}>
                    admin@smartmediaclarity.com
                  </Box>
                </Typography>
              </Stack>
            </Box>
          </Section>

        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
